import { useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { ThemeMode } from 'config';
import OrderMessagesDialog from 'components/OrderMessagesDialog';

// assets
import { BellOutlined, CheckCircleOutlined, MessageOutlined } from '@ant-design/icons';

// signalR context
import { useSignalR, Message } from 'contexts/SignalRContext';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

export default function Notification() {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [selectedOrderSeqId, setSelectedOrderSeqId] = useState<string | null>(null);

  // --- Bring in SignalR messages ---
  const { messages, setMessages } = useSignalR();
  const unreadCount = messages.length; // For the badge
  
  // Sort messages to show newest first (reverse chronological order)
  const sortedMessages = useMemo(() => {
    return [...messages].reverse();
  }, [messages]);

  // Toggle notification menu
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  // Close menu if click is outside
  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Mark all read => clear messages
  const handleMarkAllRead = () => {
    setMessages([]);
  };

  // When a user clicks on a notification, navigate to /order-edit with orderSeqId
  // and remove that message from the array + session storage
  const handleNotificationClick = (orderSeqId?: string) => {
    setOpen(false); // Close dropdown
    if (!orderSeqId) return;

    // Filter out the clicked message
    setMessages((prevMessages) => {
      const updated = prevMessages.filter((msg) => msg.data?.orderSeqId !== orderSeqId);
      return updated;
    });

    // 2️⃣ Navigate to order-edit
    navigate("/order-edit", { state: { orderSeqId } });
  };
  
  // When a user clicks on the messages icon, open the messages dialog
  const handleViewMessages = (event: React.MouseEvent, orderSeqId?: string) => {
    event.stopPropagation(); // Prevent the notification click handler from firing
    if (!orderSeqId) return;
    
    setSelectedOrderSeqId(orderSeqId);
    setMessagesDialogOpen(true);
  };

  // We style the popper and paper to be larger & scrollable
  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

  // Virtualized list item height
  const ITEM_HEIGHT = 90; // Approximate height of each notification item

  // Render function for virtualized list items
  const renderRow = ({ index, style }: ListChildComponentProps) => {
    const msg = sortedMessages[index];
    
    return (
      <Box style={style} key={index}>
        <ListItemButton 
          selected
          onClick={() => handleNotificationClick(msg.data?.orderSeqId)}
        >
          <ListItemAvatar>
            <Tooltip title="View Order Messages">
              <IconButton
                size="small"
                onClick={(e) => handleViewMessages(e, msg.data?.orderSeqId)}
                sx={{ padding: '4px' }}
              >
                <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                  <MessageOutlined />
                </Avatar>
              </IconButton>
            </Tooltip>
          </ListItemAvatar>

          <ListItemText
            disableTypography
            // 1) Primary text: orderSeqId - orderStatus - updatedBy
            primary={
              <Typography variant="h6" component="span">
                {msg.data?.orderSeqId} - {msg.data?.orderStatus} - {msg.data?.updatedBy}
              </Typography>
            }

            // 2) Secondary text: left = msgType, right = date/time
            secondary={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Left side: message type */}
                <Typography variant="caption" component="span">{msg.data?.msgType}</Typography>

                {/* Right side: date/time */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" component="span">
                    {msg.msgTime.getMonth() + 1}/
                    {msg.msgTime.getDate()}/
                    {msg.msgTime.getFullYear()}
                  </Typography>
                  <Typography variant="caption" component="span">
                    {msg.msgTime.getHours()}:{msg.msgTime.getMinutes()}:{msg.msgTime.getSeconds()}
                  </Typography>
                </Box>
              </Box>
            }
          />
        </ListItemButton>
        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : 'transparent' }}
        aria-label="open notification"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>

      {/* Notification Menu Popper */}
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [{ name: 'offset', options: { offset: [matchesXs ? -5 : 0, 9] } }]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                // Make menu larger
                minWidth: 320,
                maxWidth: 420,
                // Enough height to show ~7 items before scrolling
                maxHeight: 500
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title="Mark all as read">
                          <IconButton color="success" size="small" onClick={handleMarkAllRead}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  {/* Use virtualized list when there are messages */}
                  {unreadCount > 0 ? (
                    <Box
                      sx={{
                        '& .MuiListItemButton-root': {
                          py: 0.5,
                          '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                          '& .MuiAvatar-root': avatarSX,
                          '& .MuiListItemSecondaryAction-root': {
                            ...actionSX,
                            position: 'relative'
                          }
                        }
                      }}
                    >
                      <FixedSizeList
                        height={Math.min(440, sortedMessages.length * ITEM_HEIGHT)} // Max height or total needed
                        width="100%"
                        itemSize={ITEM_HEIGHT}
                        itemCount={sortedMessages.length}
                        overscanCount={5} // Render a few extra items for smooth scrolling
                      >
                        {renderRow}
                      </FixedSizeList>
                    </Box>
                  ) : (
                    <List
                      component="nav"
                      sx={{ p: 0 }}
                    >
                      <ListItemButton>
                        <ListItemText primary="No new notifications" />
                      </ListItemButton>
                      <Divider />
                    </List>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      
      <OrderMessagesDialog
        open={messagesDialogOpen}
        onClose={() => {
          setMessagesDialogOpen(false);
          setSelectedOrderSeqId(null);
        }}
        orderSeqId={selectedOrderSeqId}
      />
    </Box>
  );
}
